// Comprime uma imagem no navegador antes do upload: redimensiona para um lado máximo e
// reencoda em WebP (fallback JPEG) com qualidade reduzida, para diminuir o custo de
// armazenamento no R2 e acelerar o envio. Nunca aumenta o arquivo: se o resultado ficar
// maior ou igual ao original (ex.: PNG já pequeno), devolve o original inalterado. Também
// devolve o original se o tipo não for imagem ou se a decodificação falhar.
export interface CompressImageOptions {
  maxDimension?: number
  quality?: number
}

export async function compressImage(file: File, options: CompressImageOptions = {}): Promise<File> {
  const maxDimension = options.maxDimension ?? 1600
  const quality = options.quality ?? 0.8
  if (!file.type.startsWith('image/')) return file

  let bitmap: ImageBitmap
  try {
    // imageOrientation 'from-image' aplica a orientação EXIF; sem isso fotos de celular
    // podem sair giradas no canvas.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return file
  }

  try {
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(bitmap, 0, 0, width, height)

    const blob = (await canvasToBlob(canvas, 'image/webp', quality))
      ?? (await canvasToBlob(canvas, 'image/jpeg', quality))
    if (!blob || blob.size >= file.size) return file

    const extension = blob.type === 'image/webp' ? 'webp' : 'jpg'
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'foto'
    return new File([blob], `${baseName}.${extension}`, { type: blob.type })
  } finally {
    bitmap.close()
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality))
}
